import { useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useInstance} from 'react-ioc';
import { Link } from 'react-router-dom';
import {RightOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import { Button, Checkbox, DatePicker, Form, Input, Radio} from 'antd'
import PhoneInput from 'antd-phone-input';
import dayjs from 'dayjs'
import _ from 'lodash';
import {observer} from 'mobx-react-lite';

import {
    ENUM_GETMONEYWWREG_SEX
} from '../../__generated__/global-types';
import {geos} from '../../config/geo';
import {DOC_OFFER_EN, DOC_OFFER_ES, DOC_POLICY_EN, DOC_POLICY_ES, LOGIN} from '../../config/routes';
import {getSession} from '../../hooks/getSession';
import {Store} from '../../model/store/Store';
import {
    createGetMoneyWwCode,
    createGetMoneyWwCodeVariables
} from '../../shared/graphql/__generated__/createGetMoneyWwCode';
import {
    createGetMoneyWwReg,
    createGetMoneyWwRegVariables
} from '../../shared/graphql/__generated__/createGetMoneyWwReg';
import {CREATE_CODE} from '../../shared/graphql/createGetMoneyKzCode';
import {CREATE_REG} from '../../shared/graphql/createGetMoneyKzReg';
import {phoneFormat} from '../../shared/helpers/phoneFormat';

import cls from './Registration.module.scss'

interface IFormValues {
    FIO: string;
    phone: any;
    birthday: string;
    sex: 'male' | 'female';
    city: string;
}

interface IForm {

    onComplete: (values: IFormValues) => void

}

export const RegForm = observer(({onComplete}: IForm) => {

    const {t, i18n} = useTranslation();
    const [form] = Form.useForm()
    const [agreePolicy, setAgreePolicy] = useState<boolean>(false)
    const [agreeOffer, setAgreeOffer] = useState<boolean>(false)

    const [errorUniqyePhone, setErrorUniqyePhone] = useState<boolean>(false)

    const {auth, geo} = useInstance(Store)

    const [createReg, createRegData] = useMutation<createGetMoneyWwReg, createGetMoneyWwRegVariables>(CREATE_REG)
    const [createCode, createCodeData] = useMutation<createGetMoneyWwCode, createGetMoneyWwCodeVariables>(CREATE_CODE)

    const onFinish = async (values: IFormValues) => {
        await createReg({
            variables: {
                session: getSession(),
                data: {
                    FIO: values.FIO,
                    phone: phoneFormat(values.phone),
                    birthday: dayjs(values.birthday).format('YYYY-MM-DD'),
                    sex: values.sex === 'male' ? ENUM_GETMONEYWWREG_SEX.male : ENUM_GETMONEYWWREG_SEX.female,
                    phoneInput: values.phone,
                    geo: geo.geo,
                }
            },
            onCompleted: async () => {
                auth.setData(values.FIO, phoneFormat(values.phone), dayjs().format(), false)
                await createCode({
                    variables: {
                        phone: phoneFormat(values.phone),
                        data: {},
                    },
                    onCompleted: () => {
                        onComplete(values)
                    }
                })
            },
            onError: (error) => {
                console.log(error.message)
                if (error.message === 'This attribute must be unique') {
                    setErrorUniqyePhone(true)
                }
            }
        })
    }

    const options = [
        {label: t('Male'), value: 'male'},
        {label: t('Female'), value: 'female'},
    ];

    // @ts-ignore
    const validator = (_, {valid}) => {
        // if (valid(true)) return Promise.resolve(); // strict validation
        if (valid(true)) return Promise.resolve(); // non-strict validation
        return Promise.reject(t('Invalid format'));
    }

    const lang = i18n.language

    return (
        <Form form={form} layout={'vertical'} size={'large'} validateTrigger={'onSubmit'} onFinish={onFinish}
              className={cls.form}>
            <Form.Item name={'FIO'} label={t('Full name')} rules={[{required: true, message: t('Please fill in')!}]}>
                <Input placeholder={t('James Bradley')!}/>
            </Form.Item>
            <Form.Item name={'phone'} label={t('Phone number')} validateFirst rules={[
                {required: true, message: t('Please fill in')!},
                {validator},
                {validator: () => {
                        if (errorUniqyePhone) {
                            return Promise.reject(t('This number is already taken'))
                        } else {
                            return Promise.resolve()
                        }
                }},
            ]}
                       // validateStatus={errorUniqyePhone ? 'error' : undefined} help={errorUniqyePhone && 'Такой номер уже занят'}
            >
                <PhoneInput enableSearch preferredCountries={_.map(geos, item => item.name.toLowerCase())} country={geo.geo!.toLowerCase()}/>
            </Form.Item>
            {errorUniqyePhone &&
                <Form.Item>
                    <p>{t('If this is your number')} <Link to={LOGIN}>{t('Login')}</Link></p>
                </Form.Item>
            }
            <Form.Item name={'birthday'} label={t('Date of birth')}
                       rules={[{required: true, message: t('Please fill in')!}]}>
                <DatePicker format={'DD.MM.YYYY'}/>
            </Form.Item>
            <Form.Item name={'sex'} label={t('Gender')} rules={[{required: true, message: t('Please fill in')!}]}>
                <Radio.Group options={options} optionType="button"/>
            </Form.Item>

            <Form.Item>
                <Checkbox checked={agreePolicy} onChange={() => setAgreePolicy(!agreePolicy)}>{t('I agree with')} <a href={lang === 'es' ? DOC_POLICY_ES : DOC_POLICY_EN}
                                                                                                     rel={'noreferrer'}
                                                                                                     target={'_blank'}>{t('the personal data processing policy')}</a></Checkbox>
                <Checkbox checked={agreeOffer} onChange={() => setAgreeOffer(!agreeOffer)}>{t('I agree with')} <a href={lang === 'es' ? DOC_OFFER_ES : DOC_OFFER_EN}
                                                                                                     rel={'noreferrer'}
                                                                                                     target={'_blank'}>{t('the terms and conditions of the offer')}</a></Checkbox>
            </Form.Item>
            <Form.Item>
                <Button type={'primary'} htmlType={'submit'} disabled={!(agreeOffer && agreePolicy)} icon={<RightOutlined/>} loading={createRegData.loading || createCodeData.loading}>
                    {t('Get code')}
                </Button>
            </Form.Item>

        </Form>
    )
})
